import React, { useState } from "react";
import { Box, Button, Typography } from "@mui/material";
import moment from "moment";
import "moment/locale/es";
import { Field, FormCard, FormGrid, SelectInput, TextInput } from "../../../components/ui/form";
import { tokens } from "../../../theme";
import { IconAlert, IconCheckCircle, IconMail } from "../../../components/ui/icons";
import { requestIvaBook } from "../../../apiRequests/post";

moment.locale("es");

const MONTHS = moment.months().map((name, index) => ({
  value: index + 1,
  label: name.charAt(0).toUpperCase() + name.slice(1),
}));

const YEARS = Array.from({ length: 6 }, (_, i) => moment().year() - i);

/**
 * Libro IVA compras y ventas (ARCA), vía tusfacturas.app.
 *
 * El endpoint no devuelve el libro: lo genera y lo manda por mail. Por eso la
 * pantalla es un pedido, no una tabla — y se dice explícitamente, para que
 * nadie espere ver los datos acá.
 */
const IvaBook = () => {
  const previous = moment().subtract(1, "month");
  const [mes, setMes] = useState(previous.month() + 1);
  const [anio, setAnio] = useState(previous.year());
  const [email, setEmail] = useState("");
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);

  const emailIsValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());

  const submit = async () => {
    setSending(true);
    setResult(null);
    try {
      const response = await requestIvaBook({
        mes: Number(mes),
        anio: Number(anio),
        email: email.trim(),
      });

      if (response?.error === "S") {
        setResult({
          ok: false,
          text: response?.errores?.join(" · ") || "tusfacturas.app rechazó el pedido.",
        });
      } else {
        setResult({
          ok: true,
          text: `El libro de ${MONTHS.find((m) => m.value === Number(mes))?.label} ${anio} va camino a ${email.trim()}. Suele tardar unos minutos.`,
        });
      }
    } catch (error) {
      setResult({ ok: false, text: error?.message || "No se pudo pedir el libro." });
    } finally {
      setSending(false);
    }
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: "16px", width: "100%" }}>
      <FormCard
        title="Libro IVA compras y ventas"
        subtitle="Lo genera ARCA a través de tusfacturas.app y llega por mail: no se muestra en pantalla."
      >
        <FormGrid columns={3}>
          <Field label="Mes" required>
            <SelectInput value={mes} onChange={(e) => setMes(e.target.value)}>
              {MONTHS.map((month) => (
                <option key={month.value} value={month.value}>
                  {month.label}
                </option>
              ))}
            </SelectInput>
          </Field>

          <Field label="Año" required>
            <SelectInput value={anio} onChange={(e) => setAnio(e.target.value)}>
              {YEARS.map((year) => (
                <option key={year} value={year}>
                  {year}
                </option>
              ))}
            </SelectInput>
          </Field>

          <Field
            label="Enviar a"
            required
            hint="Suele ser la casilla del contador."
            error={email && !emailIsValid ? "Revisá la dirección de mail." : undefined}
          >
            <TextInput
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="contador@estudio.com.ar"
              invalid={Boolean(email) && !emailIsValid}
            />
          </Field>
        </FormGrid>

        <Box sx={{ display: "flex", justifyContent: { xs: "stretch", md: "flex-end" } }}>
          <Button
            variant="contained"
            color="primary"
            disabled={!emailIsValid || sending}
            onClick={submit}
            startIcon={<IconMail size={16} />}
            sx={{ minHeight: 44, minWidth: 220, width: { xs: "100%", md: "auto" } }}
          >
            {sending ? "Pidiendo…" : "Pedir el libro por mail"}
          </Button>
        </Box>

        {result && (
          <Box
            sx={{
              display: "flex",
              alignItems: "flex-start",
              gap: "10px",
              padding: "12px 14px",
              borderRadius: `${tokens.radiusCard}px`,
              color: result.ok ? "var(--sup-state-ok-ink)" : "var(--sup-state-danger-ink)",
              backgroundColor: result.ok ? "var(--sup-state-ok-bg)" : "var(--sup-state-danger-bg)",
            }}
          >
            <Box sx={{ display: "flex", flexShrink: 0, paddingTop: "1px" }}>
              {result.ok ? <IconCheckCircle size={18} /> : <IconAlert size={18} />}
            </Box>
            <Typography sx={{ fontSize: "13px", lineHeight: 1.45 }}>{result.text}</Typography>
          </Box>
        )}
      </FormCard>

      <Typography sx={{ fontSize: "11.5px", color: tokens.ink3, paddingX: "2px", lineHeight: 1.5 }}>
        El libro sale de lo facturado en ARCA, no de la base del sistema. Si una orden
        todavía no se facturó, no aparece en el libro.
      </Typography>
    </Box>
  );
};

export default IvaBook;
